/**
 * Choix de l'utilisateur en matière de traceurs (bandeau cookies).
 *
 * - Google Analytics ne se charge qu'avec un accord explicite (« analytics »),
 *   et seulement si `PUBLIC_GA_MEASUREMENT_ID` est configurée.
 * - Les statistiques internes (`lib/analytics-client.ts`) relèvent de la mesure
 *   d'audience exemptée de consentement : actives par défaut, l'utilisateur
 *   peut s'y opposer (« internalStats »).
 *
 * Le choix vit dans un cookie de premier rang, lisible côté client, gardé
 * six mois — au-delà, le bandeau redemande. Il est posé sur le domaine du
 * site (`.hune-app.com`) : un seul choix vaut pour le site vitrine et l'app,
 * qui en tiennent chacun une copie du même format (`site/src/scripts/consent.ts`).
 * Module neutre (aucune dépendance serveur) : importable par les composants client.
 */

import { SITE_URL } from "@shared/lib/hosts";

export const CONSENT_COOKIE = "hune-consent";
/** Incrémenter quand une nouvelle finalité apparaît : le bandeau redemande. */
export const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_SECONDS = 182 * 24 * 60 * 60;

const CHANGE_EVENT = "hune:consent-change";
const OPEN_EVENT = "hune:consent-open";

const rawMeasurementId = process.env.PUBLIC_GA_MEASUREMENT_ID?.trim() ?? "";
/** Identifiant GA4 (« G-XXXXXXX »), vide si non configuré ou mal formé. */
export const GA_MEASUREMENT_ID = /^G-[A-Z0-9]+$/.test(rawMeasurementId) ? rawMeasurementId : "";

export type ConsentChoices = { analytics: boolean; internalStats: boolean };
export type ConsentRecord = ConsentChoices & { version: number; savedAt: string };

export const DEFAULT_CHOICES: ConsentChoices = { analytics: false, internalStats: true };

/**
 * Domaine du cookie : celui du site quand on est sur lui ou l'un de ses
 * sous-domaines (`app.`), rien sinon (localhost partage déjà ses cookies
 * entre les ports).
 */
export function consentCookieDomain(hostname: string, siteUrl = SITE_URL) {
  let base = "";
  try {
    base = new URL(siteUrl).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
  if (!base.includes(".") || /^[\d.]+$/.test(base)) return "";
  return hostname === base || hostname.endsWith(`.${base}`) ? base : "";
}

/**
 * Valeur brute du cookie, ou chaîne vide s'il n'est pas posé. Une ancienne
 * copie propre à l'hôte peut coexister avec celle du domaine : la valide gagne.
 */
export function readConsentCookie() {
  if (typeof document === "undefined") return "";
  const prefix = `${CONSENT_COOKIE}=`;
  const entries = document.cookie
    .split("; ")
    .filter((part) => part.startsWith(prefix))
    .map((part) => part.slice(prefix.length));
  return entries.find((value) => parseConsent(value)) ?? entries[0] ?? "";
}

/** Choix enregistrés et toujours valides, ou null (le bandeau doit s'afficher). */
export function parseConsent(raw: string): ConsentRecord | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(decodeURIComponent(raw)) as Partial<ConsentRecord>;
    if (value.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      analytics: value.analytics === true,
      internalStats: value.internalStats !== false,
      savedAt: typeof value.savedAt === "string" ? value.savedAt : "",
    };
  } catch {
    return null;
  }
}

export function currentConsent() {
  return parseConsent(readConsentCookie());
}

export function saveConsent(choices: ConsentChoices) {
  const record: ConsentRecord = { version: CONSENT_VERSION, ...choices, savedAt: new Date().toISOString() };
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domain = consentCookieDomain(window.location.hostname);
  // La copie propre à l'hôte, s'il en reste une, ne doit plus masquer la nouvelle.
  if (domain) document.cookie = `${CONSENT_COOKIE}=; Path=/; Max-Age=0`;
  document.cookie =
    `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(record))}; Path=/; Max-Age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}${domain ? `; Domain=${domain}` : ""}`;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Abonnement pour `useSyncExternalStore` : se déclenche à chaque enregistrement. */
export function subscribeConsent(callback: () => void) {
  window.addEventListener(CHANGE_EVENT, callback);
  return () => window.removeEventListener(CHANGE_EVENT, callback);
}

/** Rouvre le panneau de préférences (lien « Gestion des cookies »). */
export function openConsentPreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onConsentPreferencesRequested(callback: () => void) {
  window.addEventListener(OPEN_EVENT, callback);
  return () => window.removeEventListener(OPEN_EVENT, callback);
}
